import { Button, Flex, Radio } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useGenerateAttendanceHealthReportMutation,
  useGenerateCostPerProductiveHourReportMutation,
  useGenerateDailyIdleTimeAbuseReportMutation,
  useGenerateEarlyComerRatioReportMutation,
  useGenerateGracePeriodAbuseReportMutation,
  useGenerateHighSalaryLowOutputPerformanceReportMutation,
  useGenerateLateGoerRatioReportMutation,
  useGenerateMonthlyDisciplineReportMutation,
  useGenerateOfficeHoursReportMutation,
  useGeneratePayrollLeakageReportMutation,
  useGenerateWorkDisciplineIssuesReportMutation,
  useGenerateWorkDisciplineScoreReportMutation,
} from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import CustomModal from '../../../../../../components/CustomModal';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';
import { getErrorMessage } from '../../../../../../lib/error';
import { toast } from '../../../../../../lib/toast';

interface ReportDownloadModalProps {
  open: boolean;
  onClose: () => void;
  filters: ManagementFilters;
}

const REPORT_OPTIONS = [
  { label: 'Today Attendance Health Report', value: 'attendance' },
  { label: 'Work Discipline Score Report', value: 'discipline_score' },
  { label: 'Work Discipline Issues Report', value: 'discipline_issues' },
  { label: 'Net office Hour x Paid Hour Report', value: 'office_hours' },
  {
    label: 'Payroll Leakage & Lost Working Hour Report',
    value: 'payroll_leakage',
  },
  { label: 'High Salary Low Output Report', value: 'high_salary_low_output' },
  { label: 'Grace Period Abuse Report', value: 'grace_period_abuse' },
  {
    label: 'Cost Per Productive Hour Report',
    value: 'cost_per_productive_hour',
  },
  { label: 'Early Comer Trend Report', value: 'early_comer_ratio' },
  { label: 'Late Goer Trend Report', value: 'late_goer_ratio' },
  { label: 'Daily Idle Time Abuse Report', value: 'daily_idle_time_abuse' },
  { label: 'Discipline Trend Monthly Report', value: 'monthly_discipline' },
];

const ReportDownloadModal = ({
  open,
  onClose,
  filters,
}: ReportDownloadModalProps) => {
  const navigate = useNavigate();
  const [selectedReport, setSelectedReport] = useState<string | null>(null);

  const [generateAttendance, { isLoading: isAttendanceLoading }] =
    useGenerateAttendanceHealthReportMutation();
  const [generateDisciplineScore, { isLoading: isDisciplineScoreLoading }] =
    useGenerateWorkDisciplineScoreReportMutation();
  const [generateDisciplineIssues, { isLoading: isDisciplineIssuesLoading }] =
    useGenerateWorkDisciplineIssuesReportMutation();
  const [generateOfficeHours, { isLoading: isOfficeHoursLoading }] =
    useGenerateOfficeHoursReportMutation();
  const [generatePayrollLeakage, { isLoading: isPayrollLeakageLoading }] =
    useGeneratePayrollLeakageReportMutation();
  const [generateHighSalaryLowOutput, { isLoading: isHighSalaryLoading }] =
    useGenerateHighSalaryLowOutputPerformanceReportMutation();
  const [generateGracePeriodAbuse, { isLoading: isGraceAbuseLoading }] =
    useGenerateGracePeriodAbuseReportMutation();
  const [generateCostPerProductiveHour, { isLoading: isCostLoading }] =
    useGenerateCostPerProductiveHourReportMutation();
  const [generateEarlyComer, { isLoading: isEarlyComerLoading }] =
    useGenerateEarlyComerRatioReportMutation();
  const [generateLateGoer, { isLoading: isLateGoerLoading }] =
    useGenerateLateGoerRatioReportMutation();
  const [generateDailyIdle, { isLoading: isDailyIdleLoading }] =
    useGenerateDailyIdleTimeAbuseReportMutation();
  const [generateMonthlyDiscipline, { isLoading: isMonthlyDisciplineLoading }] =
    useGenerateMonthlyDisciplineReportMutation();

  const isGenerating =
    isAttendanceLoading ||
    isDisciplineScoreLoading ||
    isDisciplineIssuesLoading ||
    isOfficeHoursLoading ||
    isPayrollLeakageLoading ||
    isHighSalaryLoading ||
    isGraceAbuseLoading ||
    isCostLoading ||
    isEarlyComerLoading ||
    isLateGoerLoading ||
    isDailyIdleLoading ||
    isMonthlyDisciplineLoading;

  const handleDownload = async () => {
    if (!selectedReport) {
      toast('error', 'Please select a report to download');
      return;
    }

    try {
      let result;
      switch (selectedReport) {
        case 'attendance':
          result = await generateAttendance({
            date: dayjs().format('YYYY-MM-DD'), // Today's date for attendance health
          }).unwrap();
          break;
        case 'discipline_score':
          result = await generateDisciplineScore({
            startDate: filters.workDiscipline.start_date || '',
            endDate: filters.workDiscipline.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        case 'discipline_issues': {
          const disciplineStart = filters.disciplineIssue.start_date;
          const disciplineEnd = filters.disciplineIssue.end_date;
          const last_days =
            disciplineStart && disciplineEnd
              ? dayjs(disciplineEnd).diff(dayjs(disciplineStart), 'day') + 1
              : 30;
          result = await generateDisciplineIssues({
            last_days,
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        }
        case 'office_hours':
          result = await generateOfficeHours({
            startDate: filters.netOfficeHour.start_date || '',
            endDate: filters.netOfficeHour.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        case 'payroll_leakage':
          result = await generatePayrollLeakage({
            startDate: filters.payrollLeakageLostWorkHour.start_date || '',
            endDate: filters.payrollLeakageLostWorkHour.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        case 'high_salary_low_output':
          result = await generateHighSalaryLowOutput({
            startDate: filters.highSalaryLowOutput.start_date || '',
            endDate: filters.highSalaryLowOutput.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        case 'grace_period_abuse':
          result = await generateGracePeriodAbuse({
            startDate: filters.graceTimeAbuse.start_date || '',
            endDate: filters.graceTimeAbuse.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        case 'cost_per_productive_hour':
          result = await generateCostPerProductiveHour({
            startDate: filters.costPerProductiveHour.start_date || '',
            endDate: filters.costPerProductiveHour.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        case 'early_comer_ratio':
          result = await generateEarlyComer({
            startDate: filters.workForceCulture.start_date || '',
            endDate: filters.workForceCulture.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        case 'late_goer_ratio':
          result = await generateLateGoer({
            startDate: filters.workForceCulture.start_date || '',
            endDate: filters.workForceCulture.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        case 'daily_idle_time_abuse':
          result = await generateDailyIdle({
            startDate: filters.workForceCulture.start_date || '',
            endDate: filters.workForceCulture.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
        case 'monthly_discipline':
          result = await generateMonthlyDiscipline({
            startDate: filters.disciplineTrend.start_date || '',
            endDate: filters.disciplineTrend.end_date || '',
            departmentId: filters.department_id,
            officeId: filters.office_id,
          }).unwrap();
          break;
      }

      if (result) {
        onClose();
        setSelectedReport(null);
        navigate('/admin/employee/report');
        toast('success', 'Report generated successfully');
      }
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to generate report'));
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      title="Download Report"
      size={500}
    >
      <div className="flex flex-col gap-6">
        <p className="text-theme-gray-300 text-sm">
          Select the report you would like to generate and download based on the
          current filters.
        </p>

        <div className="bg-theme-slate-800 p-4 rounded-lg border border-theme-gray-700">
          <Radio.Group
            className="flex flex-col gap-1 w-full h-[300px] overflow-y-auto"
            value={selectedReport}
            onChange={(e) => setSelectedReport(e.target.value)}
          >
            {REPORT_OPTIONS.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                className="text-white hover:bg-white/5 p-2 rounded-md transition-colors"
              >
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        </div>

        <Flex
          justify="end"
          gap={12}
          className="pt-4 border-t border-theme-gray-700"
        >
          <Button
            type="primary"
            onClick={handleDownload}
            className="btn-primary"
            disabled={!selectedReport}
            loading={isGenerating}
          >
            Download Report
          </Button>
        </Flex>
      </div>
    </CustomModal>
  );
};

export default ReportDownloadModal;
